/**
 * «Записи» — `GET /api/appointments/report`, `GET /api/appointments/people`,
 * `GET /api/appointments/staff` (по сотрудникам и способу записи).
 * Спека `docs/specs/2026-09-30-appointments-design.md`.
 */
import { apiFetch } from "@/lib/api";
import type { ZapisWhere } from "@/lib/bonus";

export type Group = "day" | "week";
export type Status = "booked" | "came" | "no_show";

export type Counts = {
  recorded: number;
  came: number;
  booked: number;
  no_show: number;
  waiting: number;
  reasons: Record<string, number>;
};

export type AppointmentsReport = {
  from: string;
  to: string;
  group: Group;
  window_days: number;
  since: string;
  records: number;
  total: Counts & { without_phone: number };
  rows: (Counts & { key: string; label: string })[];
  reasons: { code: string; label: string; count: number }[];
};

export type Person = {
  phones: number[];
  deals: number[];
  /** Лиды, из которых выросли сделки человека: в списке — ссылки на карточки. */
  leads: number[];
  recorded_at: string;
  status: Status;
  reason: string;
  reason_label: string;
  visit_at: string | null;
  booking_at: string | null;
};

export type AppointmentsPeople = { from: string; to: string; total: number; people: Person[] };

export const loadAppointments = (p: { from?: string | undefined; to?: string | undefined; group: Group }) => {
  const q = new URLSearchParams({ group: p.group });
  if (p.from) q.set("from", p.from);
  if (p.to) q.set("to", p.to);
  return apiFetch<AppointmentsReport>(`/api/appointments/report?${q}`);
};

export const loadAppointmentPeople = (p: {
  from: string;
  to: string;
  status?: Status | undefined;
  reason?: string | undefined;
}) => {
  const q = new URLSearchParams({ from: p.from, to: p.to });
  if (p.status) q.set("status", p.status);
  if (p.reason) q.set("reason", p.reason);
  return apiFetch<AppointmentsPeople>(`/api/appointments/people?${q}`);
};

/** Способ записи: звонок разделён — входящий, исходящий по лиду (сделки ещё не было), исходящий по сделке. */
export type StaffMethod = "call_in" | "call_lead" | "call_deal" | "chat" | "link" | "other";

/** Числа одной строки разреза по сотрудникам: люди, не записи. */
export type StaffCounts = {
  recorded: number;
  came: number;
  booked: number;
  no_show: number;
  waiting: number;
  /** Премия «Запись.Бронь» за брони этих людей, ₽ — в строке того, кто её получил. */
  bonus: number;
};

/** `user` — id сотрудника строкой, `auto` — клиент записался сам, `none` — автор не определён. */
export type StaffRow = StaffCounts & { user: string; name: string; methods: Record<StaffMethod, StaffCounts> };

export type StaffPerson = Person & {
  user: string;
  user_name: string | null;
  where: ZapisWhere;
  method: StaffMethod;
  channel: string | null;
  /** Запись, по которой человек отнесён к сотруднику: сделка и время. */
  zapis_deal: number;
  zapis_at: string;
  /** Разговор, после которого записали (способ — звонок), и его начало. */
  call_id: number | null;
  call_at: string | null;
  /** Чат записи: откуда поставили стадию или отправили ссылку, иначе — где ответили клиенту перед записью. */
  chat_id: number | null;
  /** Премия «Запись.Бронь» по сделкам человека: всего и кому. */
  bonus: number;
  bonus_to: { user: string; name: string; amount: number }[];
};

export type AppointmentsStaff = {
  from: string;
  to: string;
  window_days: number;
  records: number;
  total: StaffCounts & { methods: Record<StaffMethod, number> };
  methods: (StaffCounts & { code: StaffMethod; label: string; short: string })[];
  staff: StaffRow[];
  people: StaffPerson[];
};

/**
 * Куда ведёт число способа: звонки — в «Звонки» со списком разговоров, после которых записали
 * (`/calls?tab=list&zapis=`), переписка и ссылка — в «Чаты» с чатами записи (`/chats?zapis=`);
 * «не определён» открыть негде. `user` — строка рейтинга, без него — все сотрудники.
 */
export function methodHref(method: StaffMethod, p: { from: string; to: string; user?: string | undefined }): string | null {
  if (method === "other") return null;
  const q = new URLSearchParams({ from: p.from, to: p.to, zapis: method });
  if (p.user) q.set("zuser", p.user);
  return method === "chat" || method === "link" ? `/chats?${q}` : `/calls?tab=list&${q}`;
}

export const loadAppointmentsStaff = (p: { from?: string | undefined; to?: string | undefined }) => {
  const q = new URLSearchParams();
  if (p.from) q.set("from", p.from);
  if (p.to) q.set("to", p.to);
  return apiFetch<AppointmentsStaff>(`/api/appointments/staff?${q}`);
};
